//core
import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import {
  Box,
  SelectChangeEvent,
  useMediaQuery,
} from "@mui/material";

//styles
import { useStyles } from "./styles";

//helpers
import {getDataIds, getGroupByOptions, getPeriod, PeriodTypes} from "../../../../../utils";

//constant
import { fullPeriodOptions } from "../../../../../constant";
import { favoriteFilterKeys } from "../../../../constants";

//selectors
import {
  selectDealsData,
  selectOptionsData, selectPlatformsData
} from "../../../../../../../../store/admin/reports/full_report/selectors";

//hooks
import { useFullReport } from "../../../../../../../../store/admin/reports/full_report/useFullReport";
import { useRegistration } from "../../../../../../../../store/common/registration/useRegistration";

//context
import { GlobalContext } from "../../../../../../../../context/GlobalContext";

//components
import CSelect from "../../../../../../../UI/Fields/CSelect";
import CDateRangePicker from "../../../../../../../UI/Fields/CDateRangePicker";
import GroupByField, { IItemOption } from "../../../GroupByField";
import MuiAutocompleteSelectLazy from "../../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import { IFullReportFilterDataType } from "../../../../types";
import MuiAutocompleteSelect from "../../../../../../../UI/Fields/MuiAutocompleteSelect";
import MuiChipsInputField from "../../../../../../../UI/Fields/MuiChipsInput";


interface IVisibleFilterProps {
  filter: IFullReportFilterDataType;
  setUpdateFilter: Dispatch<SetStateAction<IFullReportFilterDataType>>;
  apiV2: boolean;
}

const VisibleFilter: React.FC<IVisibleFilterProps> = ({
  filter,
  setUpdateFilter,
  apiV2 = false,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

  const {
	  getFiltersField,
	  getTags,
  } = useFullReport();
	const { countries } = useRegistration();

	const context = useContext(GlobalContext);

  const optionsData = useSelector(selectOptionsData);

  //state
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypes>(filter.period);
  const [startDate, setStartDate] = useState<Date | ''>(getPeriod(filter.period).start);
  const [endDate, setEndDate] = useState<Date | ''>(getPeriod(filter.period).end);

	const [dataCountries, setDataCountries] = useState<any | null>(null);

	const [registrationFrom, setRegistrationFrom] = useState<Date | ''>(!!filter.registrationFrom ? new Date(filter.registrationFrom) : '');
  const [registrationTo, setRegistrationTo] = useState<Date | ''>(!!filter.registrationTo ? new Date(filter.registrationTo) : '');

  const [firstDepositFrom, setFirstDepositFrom] = useState<Date | ''>(!!filter.firstDepositFrom ? new Date(filter.firstDepositFrom) : '');
  const [firstDepositTo, setFirstDepositTo] = useState<Date | ''>(!!filter.firstDepositTo ? new Date(filter.firstDepositTo) : '');

  const [cpaQualificationFrom, setCpaQualificationFrom] = useState<Date | ''>(!!filter.cpaQualificationFrom ? new Date(filter.cpaQualificationFrom) : '');
  const [cpaQualificationTo, setCpaQualificationTo] = useState<Date | ''>(!!filter.cpaQualificationTo ? new Date(filter.cpaQualificationTo) : '');

  const [groupBy, setGroupBy] = useState<IItemOption[]>(filter.group_by.fields);

  //favorite fields state
	const [favAdmFRTrackerIds, setFavAdmFRTrackerIds] = useLocalStorage<boolean>(favoriteFilterKeys.trackerIds, false);
	const [favAdmFRCreativeIds, setFavAdmFRCreativeIds] = useLocalStorage<boolean>(favoriteFilterKeys.creativeIds, false);
	const [favAdmFRBrands, setFavAdmFRBrands] = useLocalStorage<boolean>(favoriteFilterKeys.brands, false);
	const [favAdmFRCurrencies, setFavAdmFRCurrencies] = useLocalStorage<boolean>(favoriteFilterKeys.currencies, false);
	const [favAdmFRDealTypes, setFavAdmFRDealTypes] = useLocalStorage<boolean>(favoriteFilterKeys.dealTypes, false);
	const [favAdmFRProductIds, setFavAdmFRProductIds] = useLocalStorage<boolean>(favoriteFilterKeys.productIds, false);
	const [favAdmFRPlatformIds, setFavAdmFRPlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
	const [favAdmFRPlayerId, setFavAdmFRPlayerId] = useLocalStorage<boolean>(favoriteFilterKeys.playerId, false);
	const [favAdmFRAnid, setFavAdmFRAnid] = useLocalStorage<boolean>(favoriteFilterKeys.anid, false);
	const [favAdmFRSource1, setFavAdmFRSource1] = useLocalStorage<boolean>(favoriteFilterKeys.source1, false);
	const [favAdmFRSource2, setFavAdmFRSource2] = useLocalStorage<boolean>(favoriteFilterKeys.source2, false);
	const [favAdmFRSource3, setFavAdmFRSource3] = useLocalStorage<boolean>(favoriteFilterKeys.source3, false);
	const [favAdmFRSource4, setFavAdmFRSource4] = useLocalStorage<boolean>(favoriteFilterKeys.source4, false);
	const [favAdmFRSource5, setFavAdmFRSource5] = useLocalStorage<boolean>(favoriteFilterKeys.source5, false);
	const [favAdmFRDeviceTypes, setFavAdmFRDeviceTypes] = useLocalStorage<boolean>(favoriteFilterKeys.deviceTypes, false);
	const [favAdmFRManagerIds, setFavAdmFRManagerIds] = useLocalStorage<boolean>(favoriteFilterKeys.managerIds, false);
	const [favAdmFRAffiliateIds, setFavAdmFRAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.affiliateIds, false);
	const [favAdmFRCostOptimizations, setFavAdmFRCostOptimizations] = useLocalStorage<boolean>(favoriteFilterKeys.costOptimizations, false);
	const [favAdmFRGeo, setFavAdmFRGeo] = useLocalStorage<boolean>(favoriteFilterKeys.geo, false);
	const [favAdmFRTrafficSources, setFavAdmFRTrafficSources] = useLocalStorage<boolean>(favoriteFilterKeys.trafficSources, false);
	const [favAdmFRTags, setFavAdmFRTags] = useLocalStorage<boolean>(favoriteFilterKeys.tags, false);

  useEffect(() => {
    if (!!selectedPeriod) {
      const period = getPeriod(selectedPeriod);

      if (!!period.start && !!period.end) {
        setStartDate(period.start);
        setEndDate(period.end);

        context.setFullReportFilter({
          ...context.fullReportFilter,
          period: selectedPeriod,
          sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
          sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
        });
        setUpdateFilter((prevState: any) => {
          return {
            ...prevState,
            period: selectedPeriod,
            sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
            sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
          }
        })
      }
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedPeriod(filter.period);
  }, [filter.period]);

  useEffect(() => {
    setGroupBy(filter.group_by.fields);
  }, [filter.group_by.fields]);

  useEffect(() => {
    setUpdateFilter((prevState) => {
      return {
        ...prevState,
        group_by: {
          fields: groupBy,
        },
      }
    })
  }, [groupBy]);

	useEffect(() => {
		if (!!countries.data) {
			const data = countries.data.reduce((acc: any[], item: any) => {
				acc.push({
					id: item.code,
					value: item.code,
				});
				return acc;
			}, []);
			setDataCountries({
				list: data,
				total_entries: data.length,
			});
		}
	}, [countries, i18n.language]);


	useEffect(() => {
    context.setFullReportFilter(filter);
  }, [filter]);

  useEffect(() => {
    setRegistrationFrom(!!filter.registrationFrom ? new Date(filter.registrationFrom) : '');
    setRegistrationTo(!!filter.registrationTo ? new Date(filter.registrationTo) : '');
  }, [filter.registrationFrom, filter.registrationTo]);

  useEffect(() => {
    setFirstDepositFrom(!!filter.firstDepositFrom ? new Date(filter.firstDepositFrom) : '');
    setFirstDepositTo(!!filter.firstDepositTo ? new Date(filter.firstDepositTo) : '');
  }, [filter.firstDepositFrom, filter.firstDepositTo]);

  useEffect(() => {
    setCpaQualificationFrom(!!filter.cpaQualificationFrom ? new Date(filter.cpaQualificationFrom) : '');
    setCpaQualificationTo(!!filter.cpaQualificationTo ? new Date(filter.cpaQualificationTo) : '');
  }, [filter.cpaQualificationFrom, filter.cpaQualificationTo]);


  const handleChangePeriodSelect = (event: SelectChangeEvent) => {
    //@ts-ignore
    const value: PeriodTypes = event.target.value;
    setSelectedPeriod(value);
  };

  const handleChangeDates = (start: Date, end: Date) => {
    const noEndDate = !end;

    setStartDate(start);
    setEndDate(noEndDate ? start : end);
    context.setFullReportFilter({
      ...context.fullReportFilter,
      period: '',
      sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
      sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
    });
    setUpdateFilter((prevState: any) => {
      return {
        ...prevState,
        period: '',
        sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
        sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
      }
    });
  };

  const fieldPayload = () => {
    return {
      page: 1,
      search: null,
      per_page: 100,
    }
  };

  const fieldTrackerIdsPayload = () => {
    return {
      page: 1,
      search: null,
      per_page: 100,
      affiliateIds: !!filter.affiliateIds.length ? getDataIds(filter.affiliateIds) : null,
    }
  };

  const fieldAffiliateIdsPayload = () => {
    return {
      page: 1,
      search: null,
      per_page: 100,
      managerIds: !!filter.managerIds.length ? getDataIds(filter.managerIds) : null,
    }
  };

  const fieldCurrenciesPayload = () => {
    return {
      page: 1,
      search: null,
      per_page: 100,
      brandCodes: !!filter.brands.length ? getDataIds(filter.brands) : null,
    }
  };

  const favoriteFields = !!dataCountries && !!optionsData && !mobile &&
  (favAdmFRTrackerIds || favAdmFRCreativeIds || favAdmFRBrands || favAdmFRCurrencies || favAdmFRDealTypes ||
	  favAdmFRProductIds || favAdmFRPlatformIds || favAdmFRPlayerId || favAdmFRAnid || favAdmFRSource1 ||
	  favAdmFRSource2 || favAdmFRSource3 || favAdmFRSource4 || favAdmFRSource5 || favAdmFRDeviceTypes ||
	  favAdmFRManagerIds || favAdmFRAffiliateIds || favAdmFRCostOptimizations || favAdmFRGeo || favAdmFRTrafficSources || favAdmFRTags)
    ? (
      <Box className={styles.favoriteFields}>
	      {optionsData.available_filters.includes("productIds") && (<>
		      {favAdmFRProductIds && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="productIds"
					      name="productIds"
					      label={t(`admin.reports.filter.productIds`)}
					      value={filter.productIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("platformIds") && (<>
		      {favAdmFRPlatformIds && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="platformIds"
					      name="platformIds"
					      label={t(`admin.reports.filter.platformIds`)}
					      value={filter.platformIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("managerIds") && (<>
		      {favAdmFRManagerIds && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="managerIds"
					      name="managerIds"
					      label={t(`admin.reports.filter.managerIds`)}
					      value={filter.managerIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      </>)}
	      </>)}
	      {optionsData.available_filters.includes("affiliateIds") && (<>
		      {favAdmFRAffiliateIds && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="affiliateIds"
					      name="affiliateIds"
					      label={t(`admin.reports.filter.affiliateIds`)}
					      value={filter.affiliateIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldAffiliateIdsPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      </>)}
	      </>)}
	      {optionsData.available_filters.includes("trackerIds") && (<>
		      {favAdmFRTrackerIds && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="trackerIds"
					      name="trackerIds"
					      label={t(`admin.reports.filter.tracker_id`)}
					      value={filter.trackerIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldTrackerIdsPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      </>)}
	      </>)}
	      {optionsData.available_filters.includes("creativeIds") && (<>
		      {favAdmFRCreativeIds && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="creativeIds"
					      name="creativeIds"
					      label={t(`admin.reports.filter.creativeIds`)}
					      value={filter.creativeIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      </>)}
	      </>)}
	      {optionsData.available_filters.includes("geo") && (<>
		      {favAdmFRGeo && apiV2 && (
			      <MuiAutocompleteSelect
				      keyValue="value"
				      keyId="id"
				      id="geo"
				      name="geo"
				      label={t(`admin.reports.filter.geo`)}
				      value={filter.geo}
				      multiple={true}
				      showSelectAll={false}
				      disabled={false}
				      staticOptions={dataCountries.list}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      maxHeightFieldScroll={32}
				      showLabelCounter={true}
				      prefix={`common.components.creative.list_geolocales.`}
			      />
		      )}
	      </>)}
	      {optionsData.available_filters.includes("currencies") && (<>
          {favAdmFRCurrencies && (
            <Box>
	            <MuiAutocompleteSelectLazy
		            keyValue="value"
		            keyId="id"
		            id="currencies"
		            name="currencies"
		            label={t(`admin.reports.filter.currencies`)}
		            value={filter.currencies}
		            multiple={true}
		            showSelectAll={false}
		            disabled={false}
		            getOptions={getFiltersField}
		            payloadData={fieldCurrenciesPayload()}
		            labelWithId={false}
		            onChange={setUpdateFilter}
		            disableCloseOnSelect={true}
		            showLabelCounter={true}
		            showNameInPayload={true}
	            />
            </Box>
          )}
        </>)}
        {optionsData.available_filters.includes("dealTypes") && (<>
          {favAdmFRDealTypes && (
            <Box>
	            <MuiAutocompleteSelectLazy
		            keyValue="value"
		            keyId="id"
		            id="dealTypes"
		            name="dealTypes"
		            label={t(`admin.reports.filter.dealTypes`)}
		            value={filter.dealTypes}
		            multiple={true}
		            showSelectAll={false}
		            disabled={false}
		            getOptions={getFiltersField}
		            payloadData={fieldPayload()}
		            labelWithId={false}
		            onChange={setUpdateFilter}
		            disableCloseOnSelect={true}
		            showLabelCounter={true}
		            showNameInPayload={true}
	            />
            </Box>
          )}
        </>)}
	      {optionsData.available_filters.includes("anid") && (<>
		      {favAdmFRAnid && (
			      <Box>
				      <MuiChipsInputField
					      id="anid"
					      name="anid"
					      label={t(`admin.reports.filter.anid`)}
					      value={filter.anid}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
        {optionsData.available_filters.includes("playerId") && (<>
          {favAdmFRPlayerId && (
            <Box>
	            <MuiChipsInputField
		            id="playerId"
		            name="playerId"
		            label={t(`admin.reports.filter.playerId`)}
		            value={filter.playerId}
		            onChange={setUpdateFilter}
	            />
            </Box>
          )}
        </>)}
	      {optionsData.available_filters.includes("source1") && (<>
		      {favAdmFRSource1 && (
			      <Box>
				      <MuiChipsInputField
					      id="source1"
					      name="source1"
					      label={t(`admin.reports.filter.source1`)}
					      value={filter.source1}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("source2") && (<>
		      {favAdmFRSource2 && (
			      <Box>
				      <MuiChipsInputField
					      id="source2"
					      name="source2"
					      label={t(`admin.reports.filter.source2`)}
					      value={filter.source2}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("source3") && (<>
		      {favAdmFRSource3 && (
			      <Box>
				      <MuiChipsInputField
					      id="source3"
					      name="source3"
					      label={t(`admin.reports.filter.source3`)}
					      value={filter.source3}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("source4") && (<>
		      {favAdmFRSource4 && (
			      <Box>
				      <MuiChipsInputField
					      id="source4"
					      name="source4"
					      label={t(`admin.reports.filter.source4`)}
					      value={filter.source4}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("source5") && (<>
		      {favAdmFRSource5 && (
			      <Box>
				      <MuiChipsInputField
					      id="source5"
					      name="source5"
					      label={t(`admin.reports.filter.source5`)}
					      value={filter.source5}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
        {optionsData.available_filters.includes("deviceTypes") && (<>
          {favAdmFRDeviceTypes && (<>
            <Box>
	            <MuiAutocompleteSelectLazy
		            keyValue="value"
		            keyId="id"
		            id="deviceTypes"
		            name="deviceTypes"
		            label={t(`admin.reports.filter.deviceTypes`)}
		            value={filter.deviceTypes}
		            multiple={true}
		            showSelectAll={false}
		            disabled={false}
		            getOptions={getFiltersField}
		            payloadData={fieldPayload()}
		            labelWithId={false}
		            onChange={setUpdateFilter}
		            disableCloseOnSelect={true}
		            showLabelCounter={true}
		            showNameInPayload={true}
	            />
            </Box>
          </>)}
        </>)}
	      {optionsData.available_filters.includes("brands") && (<>
		      {favAdmFRBrands && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="brands"
					      name="brands"
					      label={t(`admin.reports.filter.brands`)}
					      value={filter.brands}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {optionsData.available_filters.includes("costOptimizations") && (<>
		      {favAdmFRCostOptimizations && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="costOptimizations"
					      name="costOptimizations"
					      label={t(`admin.reports.filter.costOptimizations`)}
					      value={filter.costOptimizations}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
					      prefix={'admin.reports.full_report.filters.'}
				      />
			      </Box>
		      </>)}
	      </>)}
	      {optionsData.available_filters.includes("trafficSources") && (<>
		      {favAdmFRTrafficSources && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="trafficSources"
					      name="trafficSources"
					      label={t(`admin.reports.filter.trafficSources`)}
					      value={filter.trafficSources}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      </>)}
	      </>)}
	      {optionsData.available_filters.includes("tagIds") && (<>
		      {favAdmFRTags && (<>
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="tags"
					      name="tags"
					      label={t(`admin.reports.filter.tags`)}
					      value={filter.tags}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getTags}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
				      />
			      </Box>
		      </>)}
	      </>)}

      </Box>
    ) : null;

  return (<>
    {!!optionsData && (
      <Box>
        <Box className={styles.wrapper}>
          <Box className={styles.box}>
            <CSelect
              name="period"
              label={t("admin.reports.filter.period")}
              value={selectedPeriod}
              options={fullPeriodOptions}
              onChange={handleChangePeriodSelect}
              prefix="common.components.periods."
            />
          </Box>
          <Box className={styles.boxDate}>
            <CDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleChangeDates}
            />
          </Box>
        </Box>
        <Box className={styles.field}>
          <GroupByField
            name="groupBy"
            value={filter.group_by.fields}
            options={getGroupByOptions(optionsData.group_by)}
            setFilter={setGroupBy}
            prefix={"admin.reports.filter.group_by_select."}
          />
        </Box>
        {favoriteFields}
      </Box>
    )}
  </>);
};

export default VisibleFilter;
